import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  SimpleForm,
  TextInput,
  required,
  useUpdate,
  useNotify,
  useRecordContext,
  useRedirect,
  DateInput,
  SelectInput,
  useTranslate,
} from 'react-admin'
import { Typography, Grid, Button, Alert, AlertTitle } from '@mui/material'
import { EditBase } from '../../components/base'
import moment from 'moment'
import 'moment/locale/th'
import {
  TEMPORARYREQUEST_FIELDS,
  TEMPORARYREQUEST_FIELDS as TMPREQ_FIELDS,
} from '../../providers/localization/constant'
import { BranchSelectInput, FormBottomToolbarWithPermission } from '../../components/form'
import { ServiceMultiSelectInput } from '../../components/form/ServiceMultiSelectInput'
import axios from '../../config/connections'
import _ from 'lodash'
import { CancelSelectInput } from '../../components/domains/temporary-request/CancelSelectInput'
import { TextInputCancelReason } from '../../components/domains/temporary-request/TextInputCancelReason'
import { useFormContext } from 'react-hook-form'
import { RolePermission } from '../roles-permissions/type'
import { useUserContextProvider } from '../../contexts/user-context'

export const FormEdit = (): JSX.Element => {
  const record = useRecordContext()
  const notify = useNotify()
  const [servicesDuration, setServicesDuration] = useState(0)
  const [availableSlot, setAvailableSlot] = useState([])
  const [selectedServices, setSelectedServices] = useState(record?.selected_services_keys)
  const [selectedDate, setSelectedDate] = useState(record?.booking_date)
  const redirect = useRedirect()
  const t = useTranslate()
  const { currentUserData } = useUserContextProvider()
  const bookingManagementPermissions = useMemo(
    () =>
      _.get(currentUserData, 'role.permissions', []).find(
        (permission: RolePermission) => permission.key === 'booking_management',
      ),
    [currentUserData],
  )
  const bookingBookManagementPermission = useMemo(
    () =>
      bookingManagementPermissions?.sub_menu?.find(
        (permission: RolePermission) =>
          permission.key === 'booking_book_temporary_request_management',
      ),
    [bookingManagementPermissions?.sub_menu],
  )

  const getAvailableBooking = async (): Promise<void> => {
    const res = await axios.get('booking/available', {
      params: {
        branch_code: record?.branch_code,
        services_key: selectedServices,
        date: moment(selectedDate).format('YYYY-MM-DD'),
      },
    })
    if (_.get(res, 'data.result.dates[0].is_close')) {
      notify(
        <Alert severity='error'>
          <AlertTitle>{t('ra.page.error')}</AlertTitle>
          สาขาถูกปิดในวันที่เลือก กรุณาเลือกวันที่อื่น
        </Alert>,
      )
    }
    const periods = _.get(res, 'data.result.dates[0].periods')
    setAvailableSlot(
      _.map(periods, (p, idx) => {
        return {
          id: idx + 1,
          name: `${p.start_time} - ${p.end_time}${!p.is_available ? ' (เต็ม)' : ''}`,
          disabled: !p.is_available,
          start: p.start_time,
          end: p.end_time,
        }
      }),
    )
  }

  const onError = useCallback(
    (error): void => {
      const errorMessage = error.response.data.message
      return notify(errorMessage || 'ra.validation.update_temporary_request_fail', {
        type: 'error',
      })
    },
    [notify],
  )

  const onSuccess = useCallback(async (): Promise<void> => {
    notify('ra.notification.update_temporary_request_success', { type: 'success' })
    redirect('/temporary-request')
  }, [notify, redirect])

  const [update] = useUpdate(undefined, undefined, { onError, onSuccess })

  const [bookingStartTime, setBookingStartTime] = useState('')
  const [bookingEndTime, setBookingEndTime] = useState('')

  const handleSubmit = (data): void => {
    const updateData = {
      branch_code: data.branch_code,
      booking_date: moment(data.booking_date).format('YYYY-MM-DD'),
      booking_start_time: bookingStartTime,
      booking_end_time: bookingEndTime,
      first_name: data.first_name,
      last_name: data.last_name,
      email: data.email,
      tel: data.tel,
      license_plate: data.license_plate,
      province_id: data.province_id,
      is_accept_terms_conditions: true,
      car_brand: data.car_brand,
      car_model: data.car_model,
      more_detail: data.more_detail,
      status: data.status,
      services_key: data.selected_services_keys,
      cancel_reason:
        data.action_type === 'sold_out'
          ? 'สินค้าขาดตลาด'
          : data.action_type === 'inconvenience'
          ? 'ลูกค้าไม่สะดวกเข้ารับบริการ'
          : data.cancel_reason,
    }
    if (moment(data.booking_date).isBefore(moment(), 'day')) {
      return notify(
        <Alert severity='error'>
          <AlertTitle>{t('ra.page.error')}</AlertTitle>
          ไม่สามารถบันทึกรายการจองด้วยวันที่ย้อนหลังได้
        </Alert>,
      )
    }
    update('booking', { id: record?.id, data: updateData })
  }

  useEffect(() => {
    getAvailableBooking()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!selectedServices || !selectedDate) {
    redirect('list', 'temporary-request')
  }
  const BookingDateInput = useCallback((): JSX.Element => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { setValue } = useFormContext()
    return (
      <DateInput
        source='booking_date'
        label={`${TEMPORARYREQUEST_FIELDS}.booking_date`}
        validate={[required()]}
        onChange={(e): void => {
          setSelectedDate(e.target.value)
          setValue('available_slot', [])
          setAvailableSlot([])
        }}
        fullWidth
      />
    )
  }, [])
  return (
    <SimpleForm
      onSubmit={handleSubmit}
      toolbar={<FormBottomToolbarWithPermission showDeleteButton={false} />}
    >
      <Grid container spacing={1} justifyContent='center'>
        <Grid container item spacing={3} xs={12} sm={8} md={6}>
          <Grid item xs={12}>
            <BranchSelectInput
              source='branch_code'
              label={`${TEMPORARYREQUEST_FIELDS}.branch_code`}
              validate={[required()]}
            />
          </Grid>
          <Grid item xs={12}>
            <BookingDateInput />
          </Grid>
          <Grid item xs={12}>
            <ServiceMultiSelectInput
              source='selected_services_keys'
              label={`${TEMPORARYREQUEST_FIELDS}.service`}
              value={selectedServices}
              allServicesButton={false}
              filterByBranchCode={record?.branch_code}
              updateTotalDuration={setServicesDuration}
              setServices={(s): void => {
                setAvailableSlot([])
                setSelectedServices(s)
              }}
              validate={[required()]}
              fullWidth
            />
          </Grid>
          <Grid
            container
            sx={{ pl: '1.7rem' }}
            alignItems={'center'}
            justifyContent='space-between'
          >
            <Grid item>
              <Typography variant='body1'>
                รวมเวลาบริการทั้งหมด : {servicesDuration} นาที
              </Typography>
            </Grid>
            <Grid item>
              <Button
                variant='contained'
                size='medium'
                onClick={getAvailableBooking}
                sx={{ width: 'fit-content' }}
                disabled={selectedServices?.length < 1}
              >
                คำนวณเวลาใหม่
              </Button>
            </Grid>
          </Grid>
          <Grid item xs={12} sx={{ mt: '10px' }}>
            <SelectInput
              source='available_slot'
              label='เลือกเวลาที่เข้ารับบริการ'
              choices={availableSlot}
              disabled={availableSlot?.length < 1}
              onChange={(e): void => {
                setBookingStartTime(_.find(availableSlot, ['id', e.target.value])['start'])
                setBookingEndTime(_.find(availableSlot, ['id', e.target.value])['end'])
              }}
              validate={[required()]}
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <TextInput
              source='first_name'
              label={`${TEMPORARYREQUEST_FIELDS}.first_name`}
              validate={[required()]}
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <TextInput
              source='last_name'
              label={`${TEMPORARYREQUEST_FIELDS}.last_name`}
              validate={[required()]}
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <TextInput
              source='email'
              label={`${TEMPORARYREQUEST_FIELDS}.email`}
              validate={[required()]}
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <TextInput
              source='tel'
              label={`${TEMPORARYREQUEST_FIELDS}.tel`}
              validate={[required()]}
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <TextInput
              source='license_plate'
              label={`${TEMPORARYREQUEST_FIELDS}.license_plate`}
              validate={[required()]}
              fullWidth
            />
          </Grid>
          {/* Province field removed — จังหวัด no longer collected (backend province_id now optional). Ref: Hide Province task tk_8ihzaxo */}
          <Grid item xs={6}>
            <TextInput
              source='car_brand'
              label={`${TEMPORARYREQUEST_FIELDS}.car_brand`}
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <TextInput
              source='car_model'
              label={`${TEMPORARYREQUEST_FIELDS}.car_model`}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextInput
              source='more_detail'
              label={`${TEMPORARYREQUEST_FIELDS}.more_detail`}
              multiline
              rows={4}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <SelectInput
              source='status'
              label={`${TMPREQ_FIELDS}.status.label`}
              choices={
                // {
                //   id: 'temporary_request',
                //   name: t(`${TMPREQ_FIELDS}.status.choices.temporary_request`),
                //   disabled: true,
                // },
                bookingBookManagementPermission?.deletable
                  ? [
                      { id: 'pending', name: t(`${TMPREQ_FIELDS}.status.choices.pending`) },
                      { id: 'confirm', name: t(`${TMPREQ_FIELDS}.status.choices.confirm`) },
                      { id: 'cancel', name: t(`${TMPREQ_FIELDS}.status.choices.cancel`) },
                    ]
                  : [
                      { id: 'pending', name: t(`${TMPREQ_FIELDS}.status.choices.pending`) },
                      { id: 'confirm', name: t(`${TMPREQ_FIELDS}.status.choices.confirm`) },
                    ]
              }
              validate={[required()]}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <CancelSelectInput />
          </Grid>
          <Grid item xs={12}>
            <TextInputCancelReason
              label={`${TMPREQ_FIELDS}.cancel_reason.message`}
              source='cancel_reason'
            ></TextInputCancelReason>
          </Grid>
        </Grid>
      </Grid>
    </SimpleForm>
  )
}

export const TemperaryRequestEdit = (): JSX.Element => {
  return (
    <EditBase
      title={'resources.temporaryrequest.edit.title'}
      redirect='list'
      listAction={{
        hasBackButton: true,
        hasShow: false,
      }}
    >
      <FormEdit />
    </EditBase>
  )
}

export default TemperaryRequestEdit
